import Immutable, { List, OrderedSet, Set } from 'immutable';
import { ImpactCategory, RawImpactCategory } from 'records/ImpactCategory';
import { Markup, MarkupId } from 'records/Markup';
import { NewRFI } from 'records/NewRFI';
import { RawRFIAnswer, RFIAnswer } from 'records/RFIAnswer';
import { RawRFIAttachment, RFIAttachment } from 'records/RFIAttachment';
import { RawUser, User, UserList } from 'records/User';
import { PageId } from './Page';
import {
  RawRFIForwardActivity,
  RFIForwardActivity,
} from './RFIForwardActivity';
import { StudioProjectFolder } from './StudioProjectFolder';

export interface ImpactCategories {
  NONE: string;
  COST: string;
  SCHEDULE: string;
  COST_AND_SCHEDULE: string;
}

export const impactCategories: ImpactCategories = {
  NONE: 'None',
  COST: 'Cost',
  SCHEDULE: 'Schedule',
  COST_AND_SCHEDULE: 'Cost & Schedule',
};

export interface StudioData {
  studioSelectedFolders: StudioProjectFolder[];
  selectedProject: string;
  studioCurrentProjectId: string;
  studioFolderParentId: string;
}

export type RFIId = string;

export interface RawRFI {
  id: RFIId;
  projectid: string;
  title: string;
  question: string;
  duedate: number;
  craeteddate: number;
  draftcreationdate: number;
  // assignee: RawUser;
  assignees: RawUser[];
  // expectedAttachmentCount: Int!
  attachments: RawRFIAttachment[];
  answer: RawRFIAnswer | null;
  pageids: PageId[];
  number: number;
  ispublished: boolean;
  submittingname: string;
  submittingemail: string;
  submittingcompany: string;
  requestinguseremail: string;
  distributionlist: string[];
  impactcategory: RawImpactCategory;
  postedmarkupids: string[];
  postedmarkups: List<Markup>;
  forwardactivity: RawRFIForwardActivity[];
  forwardinglist: string[];
  discussionattachmentids: string[];
  previousversionid: string;
  nextversionid: string;
  versionnumber: number;
  assigneeEmail: string;
}

export interface RFIData {
  id: RFIId;
  projectid: string;
  title: string;
  question: string;
  duedate: number;
  craeteddate: number;
  draftcreationdate: number;
  // assignee: User;
  assignees: UserList;
  attachments: List<RFIAttachment>;
  answer: RFIAnswer | null;
  pageids: Set<PageId>;
  number: number;
  ispublished: boolean;
  submittingname: string;
  submittingemail: string;
  submittingcompany: string;
  requestinguseremail: string;
  distributionlist: OrderedSet<string>;
  impactcategory: ImpactCategory;
  postedmarkupids: Set<MarkupId>;
  postedmarkups: List<Markup>;
  forwardactivity: List<RFIForwardActivity>;
  forwardinglist: Set<string>;
  discussionattachmentids: Set<string>;
  previousversionid: string;
  nextversionid: string;
  versionnumber: number;
  assigneeEmail: string;
}

const defaultValues: RFIData = {
  id: '',
  projectid: '',
  title: '',
  question: '',
  duedate: 0,
  craeteddate: 0,
  draftcreationdate: 0,
  assignees: List<User>(),
  attachments: List<RFIAttachment>(),
  answer: null,
  pageids: Set<PageId>(),
  number: 0,
  ispublished: false,
  submittingname: '(empty)',
  submittingemail: '(empty)',
  submittingcompany: '(empty)',
  requestinguseremail: '(empty)',
  distributionlist: OrderedSet<string>(),
  impactcategory: new ImpactCategory(),
  postedmarkupids: Set<MarkupId>(),
  postedmarkups: List<Markup>(),
  forwardactivity: List<RFIForwardActivity>(),
  forwardinglist: Set<string>(),
  discussionattachmentids: Set<string>(),
  previousversionid: '',
  nextversionid: '',
  versionnumber: 0,
  assigneeEmail: '',
};

export const RFIFactory = Immutable.Record(defaultValues, 'RFI');

export class RFI extends RFIFactory {
  toNewRFI(): NewRFI {
    return new NewRFI({
      ...this.toObject(),
      pageids: Set<string>(this.pageids),
      assignees: this.assignees.map((a) => a.email),
      attachmentcount: this.attachments.size,
      impactcategory: this.impactcategory.kind,
    });
  }

  serialize(): RawRFI {
    return this.toJS();
  }

  static deserialize(rawRFI: RawRFI): RFI {
    const answer = rawRFI.answer ? RFIAnswer.deserialize(rawRFI.answer) : null;
    const attachments = List<RFIAttachment>(
      rawRFI.attachments?.map(RFIAttachment.deserialize)
    );
    const forwardactivity = List<RFIForwardActivity>(
      rawRFI.forwardactivity?.map(RFIForwardActivity.deserialize)
    );

    return new RFI({
      ...rawRFI,
      assignees: List<User>(rawRFI.assignees?.map(User.deserialize) || []),
      assigneeEmail:
        rawRFI.assignees.length > 0 ? rawRFI.assignees[0].email : '',
      answer,
      attachments,
      forwardactivity,
      pageids: Set<PageId>(rawRFI.pageids || []),
      distributionlist: OrderedSet(rawRFI.distributionlist),
      impactcategory: ImpactCategory.deserialize(rawRFI.impactcategory),
      postedmarkupids: Set(rawRFI.postedmarkupids),
      forwardinglist: Set<string>(rawRFI.forwardinglist),
      discussionattachmentids: Set<string>(rawRFI.discussionattachmentids),
      postedmarkups: List<Markup>(rawRFI.postedmarkups),
    });
  }
}

export type RFIList = Immutable.List<RFI>;

export type RFIOrderedMap = Immutable.OrderedMap<RFIId, RFI>;
